import type {AccessTokenService} from '@/domain/interfaces/AccessTokenService';
import type {AuthService} from '@/domain/interfaces/AuthService';

export type LoginErrorCode =
  | 'MISSING_CREDENTIALS'
  | 'INVALID_CREDENTIALS'
  | 'LOGIN_FAILED_REDIRECT'
  | 'NETWORK_ERROR'
  | 'UNEXPECTED_ERROR'
  | 'UNKNOWN_ERROR';

export type LoginResult = {code: LoginErrorCode} | undefined;

export class LoginUseCase {
  constructor(
    private readonly credentialsRepository: AccessTokenService,
    private readonly authRepository: AuthService,
  ) {}
  execute = async (email: string, password: string): Promise<LoginResult> => {
    try {
      // Validar inputs
      if (!email?.trim() || !password?.trim()) {
        return Promise.resolve({
          code: 'MISSING_CREDENTIALS',
        });
      }

      const login = await this.authRepository.create({email, password});

      // Login exitoso
      await this.credentialsRepository.set(login.access_token);
      return Promise.resolve(undefined);
    } catch (error) {
      console.error('Error in loginUseCase:', error);

      if (error && typeof error === 'object' && 'statusCode' in error) {
        const errorWithStatusCode = error as {statusCode: number};
        if (errorWithStatusCode.statusCode >= 300 && errorWithStatusCode.statusCode < 400) {
          return Promise.resolve({
            code: 'LOGIN_FAILED_REDIRECT',
          });
        }
        if (
          errorWithStatusCode.statusCode === 400 ||
          errorWithStatusCode.statusCode === 401 ||
          errorWithStatusCode.statusCode === 403
        ) {
          return Promise.resolve({
            code: 'INVALID_CREDENTIALS',
          });
        }
        if (errorWithStatusCode.statusCode >= 402 && errorWithStatusCode.statusCode < 600) {
          return Promise.resolve({
            code: 'UNEXPECTED_ERROR',
          });
        }
      }

      // Distinguir entre diferentes tipos de errores
      if (error instanceof TypeError && error.message.includes('fetch')) {
        return Promise.resolve({
          code: 'NETWORK_ERROR',
        });
      }

      if (error instanceof Error) {
        return Promise.resolve({
          code: 'UNEXPECTED_ERROR',
        });
      }

      return Promise.resolve({
        code: 'UNKNOWN_ERROR',
      });
    }
  };
}
